import React, { useEffect, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import { useRecall } from '../hooks/useRecall';

const RIPE_TARGET = 3;

/**
 * Returns the Tailwind classes for a single progress dot.
 * - Butter when the topic is ripe.
 * - Lighter butter when the dot is within the current recall progress.
 * - Grey otherwise.
 * @param {number} index - Zero-based dot position.
 * @param {number} recallCount - Current recall count.
 * @param {boolean} isRipe - Whether the topic has reached ripeness.
 * @returns {string}
 */
const getDotClass = (index, recallCount, isRipe) => {
  if (isRipe) return 'bg-butter-500';
  if (index < recallCount) return 'bg-butter-400';
  return 'bg-neutral-200';
};

/**
 * Builds the contextual hint shown beneath the recall question.
 * @param {{ recallCount: number, isRipe: boolean }} params
 * @returns {string}
 */
const getHintText = ({ recallCount, isRipe }) => {
  if (isRipe) return 'Ripe. Keep recalling to keep it fresh.';
  if (recallCount === 0) return 'Recall 3 times to ripen this topic.';

  const remaining = RIPE_TARGET - recallCount;
  const noun = remaining === 1 ? 'recall' : 'recalls';
  return `${remaining} more ${noun} to ripen this topic.`;
};

/**
 * Recall ripeness check for a single topic.
 *
 * Surfaces progress toward ripeness (3 recalls), the daily recall question,
 * and the positive/negative recall actions backed by the useRecall hook.
 *
 * @param {{
 *   topicId: string,
 *   topicName: string,
 *   onRecallComplete?: () => void,
 *   hasNext?: boolean,
 * }} props
 * @returns {JSX.Element}
 */
const RecallCheck = ({
  topicId,
  topicName,
  onRecallComplete,
  hasNext = true,
}) => {
  const { recallCount, isRipe, loading, error, record } = useRecall(topicId);

  // Briefly pop the dot that just filled when the count increases.
  const prevRecallCountRef = useRef(recallCount);
  const [poppedDotIndex, setPoppedDotIndex] = useState(null);

  useEffect(() => {
    if (recallCount > prevRecallCountRef.current) {
      setPoppedDotIndex(recallCount - 1);
      const timer = setTimeout(() => setPoppedDotIndex(null), 320);
      prevRecallCountRef.current = recallCount;
      return () => clearTimeout(timer);
    }
    prevRecallCountRef.current = recallCount;
    return undefined;
  }, [recallCount]);

  // Optional auto-advance: after a successful record, pause briefly so the dot
  // pop and "Next one" cue land, then tell the parent to move on.
  const advanceTimerRef = useRef(null);
  const [isAdvancing, setIsAdvancing] = useState(false);

  useEffect(() => {
    setIsAdvancing(false);
    return () => {
      if (advanceTimerRef.current) {
        clearTimeout(advanceTimerRef.current);
        advanceTimerRef.current = null;
      }
    };
  }, [topicId]);

  const scheduleAdvance = () => {
    if (!onRecallComplete || advanceTimerRef.current) return;
    setIsAdvancing(true);
    advanceTimerRef.current = setTimeout(() => {
      advanceTimerRef.current = null;
      onRecallComplete();
    }, 800);
  };

  const handleRecallYes = async () => {
    const success = await record(true);
    if (success) scheduleAdvance();
  };

  const handleRecallNo = async () => {
    const success = await record(false);
    if (success) scheduleAdvance();
  };

  const dots = Array.from({ length: RIPE_TARGET });
  const shownCount = Math.max(0, recallCount);
  const hintText = getHintText({ recallCount, isRipe });

  const wrapperClass = `pt-4 mt-4 border-t ${isRipe ? 'border-[#ECDFB8]' : 'border-neutral-200'}`;

  return (
    <div className={wrapperClass}>
      <div className="flex items-center justify-between gap-3 mb-2">
        <div
          className="flex items-center gap-2"
          role="img"
          aria-label={`Recall score: ${recallCount}, target ${RIPE_TARGET}`}
        >
          {dots.map((_, index) => (
            <span
              key={index}
              aria-hidden="true"
              className={`h-3 w-3 rounded-full transition-colors ${getDotClass(index, shownCount, isRipe)} ${poppedDotIndex === index ? 'recall-dot-pop' : ''}`}
            />
          ))}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-neutral-400">
            {recallCount} recall{recallCount === 1 ? '' : 's'}
          </span>
          {isRipe && (
            <span className="inline-flex items-center gap-1 rounded-full bg-[#C9982E] text-neutral-900 text-[11px] font-medium px-2 py-0.5">
              <Check size={13} aria-hidden="true" />
              Ripe
            </span>
          )}
        </div>
      </div>

      <p className="text-[13px] text-neutral-500 mb-0.5">
        Did you recall why it's in this basket?
      </p>
      <p className={`text-[11px] min-h-[14px] mb-3 ${isRipe ? 'text-[#7A5F15]' : 'text-neutral-400'}`}>{hintText}</p>

      {isAdvancing ? (
        <div
          role="status"
          className="flex items-center justify-center gap-2 py-2 text-[13px] text-neutral-500"
          aria-label={hasNext ? 'Next one in a moment' : 'Saving recall'}
        >
          <span
            className="inline-block h-1.5 w-1.5 rounded-full bg-butter-500 animate-pulse"
            aria-hidden="true"
          />
          {hasNext ? 'Next one in a moment…' : null}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={handleRecallYes}
            disabled={loading}
            aria-label={`Yes, I can recall ${topicName}`}
            className="flex items-center justify-center gap-1.5 rounded-full bg-green-600 text-white text-[13px] font-medium py-2 border-b-[3px] border-green-700 transition-transform duration-75 active:translate-y-[2px] active:border-b-[1px] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Check size={15} aria-hidden="true" />
            Yes
          </button>
          <button
            type="button"
            onClick={handleRecallNo}
            disabled={loading}
            aria-label={`Not this time, I cannot recall ${topicName}`}
            className="rounded-full border border-neutral-300 bg-transparent text-neutral-600 text-[13px] font-medium py-2 hover:bg-neutral-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Not this time
          </button>
        </div>
      )}

      {error && (
        <p role="alert" className="mt-3 text-sm text-cranberry-700">
          Something went wrong saving your recall. Please try again.
        </p>
      )}
    </div>
  );
};

export default RecallCheck;
